import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMemberAccessToken } from '../auth/supabase';
import {
  addAsset,
  checkoutDeployment,
  createInitialTrackingState,
  hydrateTrackingState,
  markAssetReady,
  returnAssets,
} from './trackingModel';
import { evaluateRecommendedDeploymentGear, evaluateSelectedDeploymentGear } from './deploymentCapacityModel';
import { reportSupplyUsage } from './deploymentUsageModel';
import { registerTrackingUsageHandler } from './trackingUsageBus';

const STORAGE_KEY = 'smfd_line_medic_tracking_v2';
const POLL_MS = 15000;

function resolveMemberCacheId(memberId) {
  const explicit = String(memberId || '').trim();
  if (explicit) return explicit;
  if (typeof window !== 'undefined') {
    const activeMember = String(window.__SMFD_MEMBER_ID__ || '').trim();
    if (activeMember) return activeMember;
  }
  return 'member';
}

function storageKey(memberId) {
  return `${STORAGE_KEY}:${String(memberId || 'member')}`;
}

function requireOnline(message) {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new Error(message || 'This action requires service. Used-supply reporting remains available offline.');
  }
}

function hydrateSharedState(raw) {
  const hydrated = hydrateTrackingState(raw);
  return {
    ...hydrated,
    revision: String(raw?.revision || ''),
    atomicRevision: Boolean(raw?.atomicRevision),
    concurrencyMode: String(raw?.concurrencyMode || ''),
    readinessCheckedAt: String(raw?.readinessCheckedAt || ''),
    readinessUnavailable: Boolean(raw?.readinessUnavailable),
  };
}

function readStoredState(memberId) {
  try {
    const raw = localStorage.getItem(storageKey(memberId));
    return { state: raw ? hydrateSharedState(JSON.parse(raw)) : hydrateSharedState(createInitialTrackingState()), hadStoredState: Boolean(raw) };
  } catch (_) {
    return { state: hydrateSharedState(createInitialTrackingState()), hadStoredState: false };
  }
}

function timestampOf(item) {
  const value = item?.updatedAt || item?.timestamp || item?.createdAt || '';
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function mergeById(first, second) {
  const merged = new Map();
  [...(first || []), ...(second || [])].forEach(item => {
    if (!item?.id) return;
    const current = merged.get(item.id);
    // The first collection is the authoritative remote side. Local only replaces it when
    // it has a genuinely newer timestamp; equal timestamps keep the remote readiness state.
    if (!current || timestampOf(item) > timestampOf(current)) merged.set(item.id, item);
  });
  return [...merged.values()];
}

function mergeStates(localState, remoteState) {
  const local = hydrateSharedState(localState);
  const remote = hydrateSharedState(remoteState);
  const events = mergeById(remote.events, local.events).sort((a, b) => timestampOf(b) - timestampOf(a));
  const incidents = [];
  [...local.recentIncidents, ...remote.recentIncidents, ...remote.deployments.map(item => item.incident), ...local.deployments.map(item => item.incident)]
    .filter(Boolean)
    .forEach(name => {
      if (!incidents.some(item => item.toLowerCase() === String(name).toLowerCase())) incidents.push(String(name));
    });
  return hydrateSharedState({
    schemaVersion: 1,
    assets: mergeById(remote.assets, local.assets),
    deployments: mergeById(remote.deployments, local.deployments).sort((a, b) => timestampOf(b) - timestampOf(a)),
    events,
    recentIncidents: incidents.slice(0, 20),
    revision: remote.revision || local.revision,
    atomicRevision: remote.atomicRevision,
    concurrencyMode: remote.concurrencyMode,
    readinessCheckedAt: remote.readinessCheckedAt,
    readinessUnavailable: remote.readinessUnavailable,
  });
}

async function memberHeaders(extra = {}) {
  const token = await getMemberAccessToken();
  if (!token) throw new Error('Member sign in required.');
  return { ...extra, Authorization: `Bearer ${token}` };
}

async function fetchRemote() {
  const response = await fetch('/api/member-tracking-state', {
    cache: 'no-store',
    headers: await memberHeaders(),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || 'Unable to load shared tracking data.');
  return hydrateSharedState(payload);
}

async function pushRemote(state, baseRevision) {
  const response = await fetch('/api/member-tracking-state', {
    method: 'POST',
    headers: await memberHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ state, baseRevision: baseRevision || state?.revision || '' }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error || 'Unable to sync shared tracking data.');
    error.code = payload?.code || '';
    error.current = payload?.current || null;
    error.readiness = payload?.readiness || null;
    throw error;
  }
  return hydrateSharedState(payload);
}

export default function useTrackingStore(memberId) {
  const cacheMemberId = resolveMemberCacheId(memberId);
  const initial = useRef(null);
  if (!initial.current) initial.current = readStoredState(cacheMemberId);

  const [state, setState] = useState(initial.current.state);
  const [sync, setSync] = useState({ status: 'connecting', lastSyncedAt: '', error: '', pending: initial.current.hadStoredState });
  const stateRef = useRef(initial.current.state);
  const dirtyRef = useRef(initial.current.hadStoredState);
  const queueRef = useRef(Promise.resolve());
  const mountedRef = useRef(true);

  const saveLocal = useCallback(next => {
    stateRef.current = next;
    localStorage.setItem(storageKey(cacheMemberId), JSON.stringify(next));
    if (mountedRef.current) setState(next);
  }, [cacheMemberId]);

  const applyRemote = useCallback(remote => {
    const next = dirtyRef.current ? mergeStates(stateRef.current, remote) : hydrateSharedState(remote);
    saveLocal(next);
    return next;
  }, [saveLocal]);

  const syncNow = useCallback(async candidate => {
    if (mountedRef.current) setSync(current => ({ ...current, status: 'syncing', error: '', pending: true }));
    try {
      const localCandidate = candidate || stateRef.current;
      let remote = await fetchRemote();
      let merged = mergeStates(localCandidate, remote);
      let saved;

      try {
        saved = await pushRemote(merged, remote.revision);
      } catch (error) {
        if (!['STALE_TRACKING_STATE', 'TRACKING_REVISION_REQUIRED'].includes(error?.code)) throw error;
        remote = error.current ? hydrateSharedState(error.current) : await fetchRemote();
        merged = mergeStates(localCandidate, remote);
        saved = await pushRemote(merged, remote.revision);
      }

      const next = mergeStates(stateRef.current, saved);
      dirtyRef.current = false;
      saveLocal(next);
      if (mountedRef.current) setSync({ status: 'synced', lastSyncedAt: new Date().toISOString(), error: '', pending: false });
      return next;
    } catch (error) {
      dirtyRef.current = true;
      if (mountedRef.current) setSync(current => ({ ...current, status: navigator.onLine ? 'error' : 'offline', error: error?.message || 'Sync failed.', pending: true }));
      throw error;
    }
  }, [saveLocal]);

  const enqueueSync = useCallback(candidate => {
    queueRef.current = queueRef.current.catch(() => {}).then(() => syncNow(candidate)).catch(() => {});
  }, [syncNow]);
  const flushSync = useCallback(() => {
    queueRef.current = queueRef.current.catch(() => {}).then(() => syncNow(stateRef.current));
    return queueRef.current;
  }, [syncNow]);


  const commit = useCallback(updater => {
    const next = hydrateSharedState(updater(stateRef.current));
    next.revision = stateRef.current.revision;
    next.atomicRevision = stateRef.current.atomicRevision;
    dirtyRef.current = true;
    saveLocal(next);
    setSync(current => ({ ...current, pending: true, status: navigator.onLine ? 'syncing' : 'offline' }));
    enqueueSync(next);
    return next;
  }, [enqueueSync, saveLocal]);

  useEffect(() => registerTrackingUsageHandler(input => commit(previous => reportSupplyUsage(previous, input))), [commit]);

  useEffect(() => {
    mountedRef.current = true;

    const pull = async () => {
      try {
        const remote = await fetchRemote();
        if (dirtyRef.current) {
          enqueueSync(mergeStates(stateRef.current, remote));
        } else {
          applyRemote(remote);
          setSync({ status: 'synced', lastSyncedAt: new Date().toISOString(), error: '', pending: false });
        }
      } catch (error) {
        setSync(current => ({ ...current, status: navigator.onLine ? 'error' : 'offline', error: error?.message || 'Shared tracking is unavailable.' }));
      }
    };

    pull();
    const interval = window.setInterval(pull, POLL_MS);
    const online = () => pull();
    const offline = () => setSync(current => ({ ...current, status: 'offline', error: 'Offline — used-supply changes stay saved on this account on this device.', pending: dirtyRef.current }));
    const visible = () => { if (document.visibilityState === 'visible') pull(); };
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', visible);

    return () => {
      mountedRef.current = false;
      window.clearInterval(interval);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [applyRemote, enqueueSync]);

  const actions = useMemo(() => ({
    addAsset: (input, actor) => {
      requireOnline('Adding accountable gear requires service.');
      return commit(previous => addAsset(previous, input, actor));
    },
    checkout: input => {
      requireOnline('Starting a deployment requires service. Offline mode is limited to reporting used supplies.');
      const deploymentType = String(input?.deploymentType || input?.role || 'EMPF').toUpperCase();
      const selectedIds = input?.assetIds || [];
      const requirementCheck = evaluateSelectedDeploymentGear(stateRef.current.assets, selectedIds, deploymentType);
      if (!requirementCheck.ready) {
        throw new Error(`Missing required ${deploymentType} gear: ${requirementCheck.missingLabels.join(', ')}.`);
      }

      const recommendedCheck = evaluateRecommendedDeploymentGear(stateRef.current.assets, selectedIds);
      if (recommendedCheck.missingLabels.length && typeof window !== 'undefined' && typeof window.confirm === 'function') {
        const missingList = recommendedCheck.missingLabels.map(label => `• ${label}`).join('\n');
        const confirmed = window.confirm(
          `You did not check out the following optional gear:\n\n${missingList}\n\nAre you sure you want to start this deployment without it?`
        );
        if (!confirmed) {
          throw new Error('Checkout cancelled. Add any optional gear you want to take, then start the deployment again.');
        }
      }

      return commit(previous => {
        const latestRequirementCheck = evaluateSelectedDeploymentGear(previous.assets, selectedIds, deploymentType);
        if (!latestRequirementCheck.ready) {
          throw new Error(`Missing required ${deploymentType} gear: ${latestRequirementCheck.missingLabels.join(', ')}.`);
        }
        return checkoutDeployment(previous, input);
      });
    },
    returnAssets: input => {
      requireOnline('Returning gear requires service so the restock request and shared gear status are recorded together.');
      return commit(previous => returnAssets(previous, input));
    },
    // This is the intentionally supported offline write. It commits to the member-scoped
    // local tracking cache immediately and the existing sync queue pushes it after reconnect.
    reportUsage: input => commit(previous => reportSupplyUsage(previous, input)),
    markReady: (assetId, actor) => {
      requireOnline('Changing gear readiness requires service.');
      return commit(previous => markAssetReady(previous, assetId, actor));
    },
    importState: next => {
      requireOnline('Importing tracking state requires service.');
      return commit(() => hydrateSharedState(next));
    },
    reset: () => {
      requireOnline('Resetting tracking state requires service.');
      return commit(() => hydrateSharedState(createInitialTrackingState()));
    },
    syncNow: () => enqueueSync(stateRef.current),
    flushSync,
  }), [commit, enqueueSync, flushSync]);

  return { state, actions, sync };
}
